import { HideContainer } from '@components/hide-container';
import { IconSms } from '@components/icons/phone-sms';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { FieldMappingVariables } from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useWebSocket } from 'api/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionValuesTypes } from 'api/types';
import { TimeSavedContainer } from 'ui/helpers';
import { useFeature } from 'ui/hooks';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { PreviewSmsProps, usePreviewSmsModal } from './preview-sms-modal';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { SMSMessageCountWarning, TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'send_sms';

type SmsData = {
  description: string;
  isAutomatic: boolean;
  recipients: string[];
  message: EditorValue<{ variables?: FieldMappingVariables }>;
  switchSpecific: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
} & TimeSavedData;

const Form = createActionForm<SmsData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<ActionType>(journeyActionId);
  const values = data?.values;
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const showPreviewSmsModal = usePreviewSmsModal();
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const formDescription = formStore.useFieldValue('description');
  const isDirty = formStore.useIsDirty();
  const formValues = formStore.useFormValues() as JourneyActionValuesTypes[ActionType];
  const isRequired = formStore.useFieldValue('executionMethod') === 'automatic';
  const recipientsError = formStore.useFieldError('recipients');
  const smsContentError = formStore.useFieldError('message');
  const baseVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: ['file', 'file_multi'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['phone'],
  });
  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: SmsData) =>
    editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'send_sms',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        recipients: data.recipients ?? [],
        message: data.message?.value ? data.message : {},
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });

  const recipients =
    formValues?.recipients?.map((recipient) =>
      recipient.variables
        ? recipient.variables[Object.keys(recipient.variables)[0]].label
        : recipient.value
    ) || [];

  const smsMessage: PreviewSmsProps = {
    to: recipients,
    textMessage: formValues?.message?.value ?? '',
    date: undefined,
  };

  const hasSmsContentError = recipientsError === 'required' || smsContentError === 'required';

  if (isLoading || !data) {
    return <ActionSidebarLoading title={t('sms')} icon={IconSms} iconColor="green100" {...props} />;
  }

  const actionDescription = formDescription || data.description;

  return (
    <Sidebar>
      <ActionHeader title={t('sms')} icon={IconSms} iconColor="green100" {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack orientation="vertical" space={6}>
            <HideContainer title={t('actionDetails')}>
              <DescriptionFormField defaultValue={data.description} />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('triggerMethod')}>
              <ExecutionMethod
                assignees={data.assignees}
                isAutomatic={data.isAutomatic}
                formStore={formStore}
                hideLabel
              />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('smsContent')} error={hasSmsContentError}>
              <Stack space={4}>
                <Form.FieldMappingPhoneListField
                  id="recipients"
                  data-cy="sms-recipients"
                  validators={isRequired ? ['required'] : []}
                  key={`recipients-${String(journeyActionId)}`}
                  variableGroups={phoneVariableGroups}
                  label={t('recipients')}
                  info={t('sendToInfo')}
                  defaultValue={values?.recipients ?? []}
                  fieldMappingId={fieldMappingId}
                />
                <Form.FieldMappingTextEditor
                  id="message"
                  data-cy="sms-message"
                  label={t('message')}
                  plaintext={true}
                  minHeight={pxToRem(104)}
                  variableGroups={baseVariableGroups}
                  defaultValue={values?.message}
                  key={`message-${String(journeyActionId)}`}
                  validators={isRequired ? ['required'] : []}
                  fieldMappingId={fieldMappingId}
                  aiOptions={
                    hasContentAIEnabled
                      ? {
                          generateChoices: actionDescription
                            ? [
                                {
                                  title: t('generateFromActionDescription'),
                                  prompt: `Write a sms message for this description: ${actionDescription}`,
                                },
                              ]
                            : [],
                          threadInstructions:
                            'The user is creating a sms message. Keep the response concise and formatted correctly, and do not include any tags such as <b> or any HTML formatting—just plain text for SMS.',
                          useWebSocket,
                        }
                      : undefined
                  }
                />
                <SMSMessageCountWarning message={formValues?.message} />
              </Stack>
            </HideContainer>
            <Sidebar.Separator />
            <TimeSavedContainer {...data.timeSaved} />
          </Stack>
          <ActionFooter
            onPreview={() => showPreviewSmsModal(smsMessage)}
            action={data}
            isDisabled={!isDirty || editMutation.isLoading}
            form={Form}
          />
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    sms: 'Send SMS',
    message: 'Message',
    recipients: 'Send to',
    sendToInfo: 'Recipient/s of the SMS. Phone numbers can be fixed or dynamic',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    smsContent: 'SMS content',
    generateFromActionDescription: 'Generate from action description',
  },
};
